import { Box, Grid, Typography, Container, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { motion, useAnimation } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { useEffect } from "react";

const VisionMission = ({ language = 'EN' }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const controls = useAnimation();
    const [ref, inView] = useInView({
        threshold: 0.2,
        triggerOnce: false
    });

    // Language content
    const content = {
        EN: {
            visionTitle: "Our Vision",
            visionText: "To become the foremost social business platform in the beauty industry & a second home for beautepreneurs",
            missionTitle: "Our Mission",
            missionItems: [
                "Support 10,000 beauty entrepreneurs with equality & integrity",
                "Enhance entrepreneurship by supplying global skills to improve the beauty industry"
            ]
        },
        BM: {
            visionTitle: "Visi Kami",
            visionText: "Untuk menjadi platform perniagaan sosial terkemuka dalam industri kecantikan & rumah kedua untuk usahawan kecantikan",
            missionTitle: "Misi Kami",
            missionItems: [
                "Menyokong 10,000 usahawan kecantikan dengan kesaksamaan & integriti",
                "Meningkatkan keusahawanan dengan menyediakan kemahiran global untuk memajukan industri kecantikan"
            ]
        },
        CN: {
            visionTitle: "我们的愿景",
            visionText: "成为美业行业首屈一指的社交商业平台，成为美业企业家的第二个家",
            missionTitle: "我们的使命",
            missionItems: [
                "以平等和诚信支持10,000名美业企业家",
                "通过提供全球技能来增强创业精神，改善美业行业"
            ]
        }
    };

    useEffect(() => {
        if (inView) {
            controls.start("visible");
        } else {
            controls.start("hidden");
        }
    }, [controls, inView]);

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.3,
                when: "beforeChildren"
            }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 40 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                type: "spring",
                stiffness: 100,
                damping: 15,
                mass: 0.5
            }
        }
    };

    return (
        <Box
            id="vision"
            ref={ref}
            sx={{
                position: 'relative',
                minHeight: isMobile ? '70vh' : '100vh',
                display: 'flex',
                alignItems: 'center',
                overflow: 'hidden'
            }}
        >
            <Box
                component={motion.div}
                initial={{ scale: 1.1 }}
                animate={controls}
                variants={{
                    hidden: { scale: 1.05, opacity: 0.8 },
                    visible: { scale: 1, opacity: 1 }
                }}
                transition={{ duration: 1.2 }}
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundImage: 'url(/images/darren.jpg)',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    zIndex: 0,
                    filter: 'brightness(0.7)'
                }}
            />

            <Container
                sx={{
                    position: 'relative',
                    zIndex: 1,
                    px: isMobile ? 2 : 4
                }}
            >
                <Grid
                    container
                    component={motion.div}
                    variants={containerVariants}
                    initial="hidden"
                    animate={controls}
                    spacing={4}
                    sx={{
                        backgroundColor: 'rgba(0,0,0,0.3)',
                        borderRadius: 4,
                        p: isMobile ? 3 : 6,
                        mx: 'auto',
                        maxWidth: isMobile ? '100%' : '90%',
                        backdropFilter: 'blur(4px)',
                        boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
                    }}
                >
                    {/* Vision Column */}
                    <Grid item xs={12} md={6}>
                        <motion.div variants={itemVariants}>
                            <Typography
                                variant={isMobile ? "h4" : "h2"}
                                gutterBottom
                                sx={{
                                    color: 'white',
                                    fontWeight: 'bold',
                                    textShadow: '0 2px 4px rgba(0,0,0,0.3)'
                                }}
                            >
                                {content[language].visionTitle}
                            </Typography>
                            <Typography
                                variant={isMobile ? "body1" : "h6"}
                                sx={{
                                    color: 'white',
                                    textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                                    lineHeight: 1.6
                                }}
                            >
                                {content[language].visionText}
                            </Typography>
                        </motion.div>
                    </Grid>

                    {/* Mission Column */}
                    <Grid item xs={12} md={6}>
                        <motion.div variants={itemVariants}>
                            <Typography
                                variant={isMobile ? "h4" : "h2"}
                                gutterBottom
                                sx={{
                                    color: 'white',
                                    fontWeight: 'bold',
                                    textShadow: '0 2px 4px rgba(0,0,0,0.3)'
                                }}
                            >
                                {content[language].missionTitle}
                            </Typography>
                            <Box component="ul" sx={{ pl: 2, mb: 2 }}>
                                {content[language].missionItems.map((item, index) => (
                                    <motion.li
                                        key={index}
                                        variants={itemVariants}
                                        style={{
                                            color: 'white',
                                            textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                                            lineHeight: 1.6,
                                            marginBottom: theme.spacing(1)
                                        }}
                                    >
                                        <Typography
                                            component="span"
                                            variant={isMobile ? "body1" : "h6"}
                                        >
                                            {item}
                                        </Typography>
                                    </motion.li>
                                ))}
                            </Box>
                        </motion.div>
                    </Grid>
                </Grid>
            </Container>
        </Box>
    );
};

export default VisionMission;